import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./LAEAI.css";

/**
 * Enlaces de las respuestas. Solo son clicables las rutas de la plataforma (dentro de la app; los documentos
 * /doc-render en pestaña nueva) y mailto:/tel:. Un enlace a una web externa se muestra como texto: si un dato
 * manipulado (p. ej. el nombre de un proveedor) intentara colar un enlace, no se puede usar para sacar datos.
 * El estilo va en LAEAI.css (.laeai-respuesta a) porque el CSS global de los enlaces usa !important.
 */
const EnlaceRespuesta = ({ href = "", children }) => {
  const origen = window.location.origin;
  const ruta = href.startsWith(origen) ? href.slice(origen.length) || "/" : href;
  const interno = ruta.startsWith("/") && !ruta.startsWith("//");
  if (interno && ruta.startsWith("/doc-render")) {
    return <a href={ruta} target="_blank" rel="noopener noreferrer">{children}</a>;
  }
  if (interno) return <Link to={ruta}>{children}</Link>;
  if (/^(mailto|tel):/i.test(href)) return <a href={href}>{children}</a>;
  return <span>{children}</span>;
};

const componentesMarkdown = {
  p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
  h1: ({ node, children, ...props }) => <h3 className="text-base font-semibold mt-3 mb-2" {...props}>{children}</h3>,
  h2: ({ node, children, ...props }) => <h3 className="text-base font-semibold mt-3 mb-2" {...props}>{children}</h3>,
  h3: ({ node, children, ...props }) => <h4 className="text-sm font-semibold mt-3 mb-1" {...props}>{children}</h4>,
  hr: ({ node, ...props }) => <hr className="my-3 border-gray-200" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-gray-300 pl-3 text-gray-600 mb-2" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2" {...props} />,
  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  a: ({ node, href, children }) => <EnlaceRespuesta href={href}>{children}</EnlaceRespuesta>,
  // Nunca cargar imágenes: una imagen externa podría filtrar datos en su URL sin que el usuario haga nada
  img: () => null,
  table: ({ node, ...props }) => (
    <div className="overflow-x-auto mb-2">
      <table className="min-w-full text-xs border-collapse" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left font-semibold" {...props} />,
  td: ({ node, ...props }) => <td className="border border-gray-200 px-2 py-1 align-top" {...props} />,
  code: ({ node, ...props }) => <code className="bg-gray-100 px-1 rounded text-xs" {...props} />,
};

/**
 * Por si el modelo escribe una ruta sin formato de enlace Markdown:
 *   "Tu IA Normativa" ([/ia-normativa])  ->  [Tu IA Normativa](/ia-normativa)
 *   [/ia-normativa]                      ->  [/ia-normativa](/ia-normativa)
 * Los enlaces ya correctos ([texto](/ruta)) no se tocan.
 */
export const arreglarEnlaces = (texto = "") => texto
  .replace(/"([^"\n]{1,60})"\s*\(\s*\[?(\/[\w\-/?=&.%#]*)\]?\s*\)/g, "[$1]($2)")
  .replace(/\[(\/[\w\-/?=&.%#]*)\](?!\()/g, "[$1]($1)");

// Enlaces como texto (con la ruta en el title): para el panel del equipo del Chat Elite, donde las rutas del AAFF
// (/comunidades/..., /proveedores/...) no existen
const componentesSinEnlaces = {
  ...componentesMarkdown,
  a: ({ node, href, children }) => <span className="laeai-enlace-texto" title={href}>{children}</span>,
};

/**
 * Respuesta del asistente en Markdown; `escribiendo` muestra el cursor mientras llega en streaming.
 * `enlacesComoTexto`: los enlaces se muestran sin poder abrirlos (vista del equipo).
 */
export default function LAEAIRespuesta({ texto, escribiendo, enlacesComoTexto = false }) {
  return (
    <>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={enlacesComoTexto ? componentesSinEnlaces : componentesMarkdown}>
        {arreglarEnlaces(texto || "")}
      </ReactMarkdown>
      {escribiendo && <span className="inline-block w-[3px] h-[15px] align-middle bg-gray-400 animate-pulse ml-0.5" />}
    </>
  );
}
